import { useState } from 'react';
import { useSession } from '../hooks/useSession';

/** Two initials for the avatar, from the business name where there is one. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return (words[0][0] + (words[1]?.[0] ?? '')).toUpperCase();
}

export function TopBar({
  query,
  onQuery,
  onMenu,
}: {
  query: string;
  onQuery: (q: string) => void;
  onMenu?: () => void;
}) {
  const { vendor, signOut } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const name = vendor?.businessName ?? vendor?.name ?? 'Vendor';
  const location = [vendor?.city, vendor?.state].filter(Boolean).join(', ');

  return (
    <header className="vu-top">
      {/* Only rendered below 900px, where the rail is an off-canvas drawer. */}
      <button type="button" className="vu-menu-btn" onClick={onMenu} aria-label="Open menu">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>

      <div className="vu-searchwrap">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          className="vu-search"
          placeholder="Search every lesson — sizing, packing, festivals, returns…"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          aria-label="Search every lesson"
        />
      </div>

      {/*
        The account menu. There was previously no way for a vendor to sign out at all —
        `signOut` existed on the session but nothing in the vendor UI ever called it, so a
        shared machine kept the session until the 30-day cookie expired.
      */}
      <div className="vu-who" style={{ position: 'relative' }}>
        <div>
          <div className="vu-who-name">{name}</div>
          <div className="vu-who-sub">Active vendor{location ? ` · ${location}` : ''}</div>
        </div>
        <button
          type="button"
          className="vu-avatar"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Account menu"
          style={{ cursor: 'pointer', border: 0, font: 'inherit' }}
        >
          {initials(name)}
        </button>

        {menuOpen && (
          <>
            {/* Click-away target, behind the menu but above the page. */}
            <button
              type="button"
              aria-label="Close account menu"
              onClick={() => setMenuOpen(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 20, background: 'transparent', border: 0, cursor: 'default' }}
            />
            <div
              role="menu"
              style={{
                position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 21,
                minWidth: 210, padding: 'var(--space-3)',
                background: 'var(--surface-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 10px 30px rgba(1, 56, 95, .16)',
              }}
            >
              <div style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)', fontSize: 'var(--fs-sm)' }}>
                {name}
              </div>
              <div className="vu-subtle" style={{ fontSize: 'var(--fs-xs)', marginBottom: 'var(--space-3)', wordBreak: 'break-all' }}>
                {vendor?.email}
              </div>
              <button
                type="button"
                role="menuitem"
                className="vu-btn vu-btn-ghost"
                style={{ width: '100%' }}
                onClick={() => void signOut()}
              >
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
